import Add from '@mui/icons-material/Add';
import { Button } from '@/ui';
import { CriteriaPaneProps, PreviousRegistrationEntry } from '../types';
import {
  CriteriaSection,
  EmptyHint,
  EntryCard,
  EntryField,
  EntryFields,
  FieldGrid,
  Input,
  MultiSelect,
  SelectIncludeExclude,
  SelectSimple,
} from '../FormComponents';
import { useAffiliateNameOrTagOptions, useDomainNameOrTagOptions } from '../useCriteriaOptions';
import { AND_OR_OPTIONS } from '../constants';
import { HELP } from '../criteriaHelp';

const toCount = (value: string): number | null => {
  const parsed = value === '' ? null : parseInt(value, 10);
  return parsed === null || isNaN(parsed) ? null : parsed;
};

export function PreviousRegistrationSection({ state, setState, errors, markFieldTouched }: CriteriaPaneProps) {
  const domainsOrTags = useDomainNameOrTagOptions();
  const affiliatesOrTags = useAffiliateNameOrTagOptions();

  const configErrors = ((errors || {})['previousRegistrationConfiguration'] as Record<string, unknown>) || {};
  const entryErrors = Array.isArray(configErrors['previousRegistrations'])
    ? (configErrors['previousRegistrations'] as Record<string, unknown>[])
    : [];

  const logic = state.previousRegistrations?.andOrLogic || '';
  const entries = state.previousRegistrations?.previousRegistrations || [];

  const setEntries = (update: (list: PreviousRegistrationEntry[]) => PreviousRegistrationEntry[]) =>
    setState((s) => {
      const pr = s.previousRegistrations;
      if (!pr) return s;
      return { ...s, previousRegistrations: { ...pr, previousRegistrations: update(pr.previousRegistrations) } };
    });

  const updateEntry = (idx: number, patch: Partial<PreviousRegistrationEntry>) =>
    setEntries((list) => list.map((e, i) => (i === idx ? { ...e, ...patch } : e)));

  const addEntry = () =>
    setEntries((list) => [
      ...list,
      {
        includeExcludeAction: 'include',
        domains: [],
        affiliates: [],
        previousRegistrationsMin: null,
        previousRegistrationsMax: null,
      },
    ]);

  return (
    <CriteriaSection
      title='Previous Registrations'
      help={HELP.previousRegistrations}
      actions={
        logic && (
          <Button color='primary' startIcon={<Add />} onClick={addEntry}>
            Add Entry
          </Button>
        )
      }
    >
      <FieldGrid min='20rem'>
        <SelectSimple
          label='AND/OR logic'
          value={logic}
          onChange={(v) =>
            setState((s) => ({
              ...s,
              previousRegistrations: {
                andOrLogic: v ? (v as 'AND' | 'OR') : null,
                previousRegistrations: s.previousRegistrations?.previousRegistrations || [],
              },
            }))
          }
          options={AND_OR_OPTIONS}
          placeholder='Select Logic'
          help={HELP.andOrLogic}
        />
      </FieldGrid>

      {logic && entries.length === 0 && <EmptyHint>No entries yet. Use Add Entry to require one.</EmptyHint>}

      {logic &&
        entries.map((entry, idx) => {
          const entryError = entryErrors[idx] || {};
          const errorFor = (key: string) =>
            typeof entryError[key] === 'string' ? (entryError[key] as string) : undefined;

          return (
            <EntryCard
              key={idx}
              title={`Entry ${idx + 1}`}
              onRemove={() => setEntries((list) => list.filter((_, j) => j !== idx))}
            >
              <EntryFields>
                <EntryField size='narrow'>
                  <SelectIncludeExclude
                    label='Include/Exclude'
                    value={entry.includeExcludeAction || 'include'}
                    onChange={(v) => updateEntry(idx, { includeExcludeAction: v })}
                    error={errorFor('includeExcludeAction')}
                  />
                </EntryField>
                <EntryField size='wide'>
                  <MultiSelect
                    label='Domains/Tags'
                    value={entry.domains ?? []}
                    onChange={(v) => updateEntry(idx, { domains: v })}
                    options={domainsOrTags}
                    disabled={!entry.includeExcludeAction}
                    error={errorFor('domains')}
                    help={HELP.previousRegistrationDomains}
                    onBlur={() =>
                      markFieldTouched(`previousRegistrationConfiguration.previousRegistrations.${idx}.domains`)
                    }
                  />
                </EntryField>
                <EntryField size='wide'>
                  <MultiSelect
                    label='Affiliates/Tags'
                    value={entry.affiliates ?? []}
                    onChange={(v) => updateEntry(idx, { affiliates: v })}
                    options={affiliatesOrTags}
                    groupsOrder={['Tags', 'Affiliates']}
                    disabled={!entry.includeExcludeAction}
                    onBlur={() =>
                      markFieldTouched(`previousRegistrationConfiguration.previousRegistrations.${idx}.affiliates`)
                    }
                  />
                </EntryField>
                <EntryField size='narrow'>
                  <Input
                    label='Min'
                    type='number'
                    value={entry.previousRegistrationsMin?.toString() ?? ''}
                    onChange={(v) => updateEntry(idx, { previousRegistrationsMin: toCount(v) })}
                    error={errorFor('previousRegistrationsMin')}
                    help={HELP.previousRegistrationCount}
                  />
                </EntryField>
                <EntryField size='narrow'>
                  <Input
                    label='Max'
                    type='number'
                    value={entry.previousRegistrationsMax?.toString() ?? ''}
                    onChange={(v) => updateEntry(idx, { previousRegistrationsMax: toCount(v) })}
                    error={errorFor('previousRegistrationsMax')}
                    help={HELP.previousRegistrationCount}
                  />
                </EntryField>
              </EntryFields>
            </EntryCard>
          );
        })}
    </CriteriaSection>
  );
}
